import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Droplets, Waves, Warehouse, HardHat, Leaf, ArrowRight, Clock, MapPin,
  Calendar, Star, Truck, Zap, ShieldCheck, Play, Phone, CheckCircle2,
  Loader2, XCircle, Wind, Siren,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceMap from "@/components/ServiceMap";
import api from "@/lib/api";

const HERO = "/hero-blakes.png";
const POOL_IMG = "/hero-1.png";
const IMG = {
  pool: "https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  tank: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  construction: "https://images.unsplash.com/photo-1783753445188-e2a27319e408?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  landscape: "https://images.unsplash.com/photo-1558904541-efa843a96f01?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const trust = [
  { icon: Zap, title: "Fast Delivery", sub: "Same day available" },
  { icon: ShieldCheck, title: "No Hidden Fees", sub: "Upfront pricing" },
  { icon: MapPin, title: "Auckland Wide", sub: "Local & reliable" },
  { icon: Truck, title: "Up to 11,000L", sub: "Large capacity" },
];
const stats = [
  { icon: Droplets, value: "11,000L", accent: "Max Capacity", desc: "Large volumes delivered", color: "bg-blue-100 text-blue-600" },
  { icon: Clock, value: "Same Day", accent: "Fast Service", desc: "When you need it", color: "bg-blue-100 text-blue-600" },
  { icon: MapPin, value: "Auckland Wide", accent: "Coverage", desc: "Residential & Commercial", color: "bg-cyan-100 text-cyan-600" },
  { icon: Calendar, value: "Easy Booking", accent: "Get a Quote", desc: "Book in seconds", color: "bg-blue-100 text-blue-600" },
];
const services = [
  { icon: Droplets, title: "Pool Fills", desc: "Fast and reliable pool filling for homes and businesses.", color: "bg-blue-600", img: IMG.pool, type: "pool" },
  { icon: Warehouse, title: "Water Tanks", desc: "Filling for residential, commercial and rural tanks.", color: "bg-teal-500", img: IMG.tank, type: "tank" },
  { icon: HardHat, title: "Construction Sites", desc: "Water for dust control, compaction and general use.", color: "bg-orange-500", img: IMG.construction, type: "construction" },
  { icon: Leaf, title: "Landscaping", desc: "Water for gardens, landscaping and large areas.", color: "bg-purple-500", img: IMG.landscape, type: "emergency" },
];
const useCases = [
  { icon: Droplets, label: "Pool Filling" },
  { icon: Warehouse, label: "Water Tanks" },
  { icon: HardHat, label: "Construction" },
  { icon: Wind, label: "Dust Suppression" },
  { icon: Leaf, label: "Landscaping" },
  { icon: Siren, label: "Emergency Supply" },
];
const whyList = ["Fast Turnaround", "Up to 11,000L Per Load", "Residential & Commercial", "Easy Online Booking", "Local Auckland Team", "Clear Upfront Pricing"];
const steps = [
  { n: "01", title: "Enter Your Address", desc: "Tell us where you need water — we'll confirm your area price instantly." },
  { n: "02", title: "Choose Your Delivery", desc: "Pick town or pure water, how many loads and a date that suits." },
  { n: "03", title: "We Deliver Your Water", desc: "Our local team arrives on time and fills you up. Simple." },
];
const reviews = [
  { name: "Sarah M.", loc: "Takapuna", text: "Blake's were fantastic! Arrived on time and filled our pool quickly. Great service and friendly team." },
  { name: "Mike T.", loc: "Kumeū", text: "Reliable tank refills every single time. The online booking makes it so easy. Highly recommend." },
  { name: "Dave R.", loc: "Riverhead", text: "We rely on Blake's for our construction site. Never let us down, always same-day when we're stuck." },
];
const BTN = { background: "linear-gradient(135deg,#087FF5,#18B9F5)", boxShadow: "0 10px 24px -8px rgba(8,127,245,0.6)" };
const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function Landing() {
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [review, setReview] = useState(0);
  const goBook = (t) => navigate(t ? `/book?service=${t}` : "/book");

  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null); // {status:'in'|'out', suburb, town_water, pure_water}
  const debRef = useRef(null);

  const onAddress = (e) => {
    const v = e.target.value;
    setAddress(v);
    setResult(null);
    if (debRef.current) clearTimeout(debRef.current);
    if (v.trim().length < 3) { setSuggestions([]); return; }
    setSearching(true);
    debRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geocode?q=${encodeURIComponent(v)}`);
        setSuggestions(data.suggestions || []);
      } catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 500);
  };

  const pickAddress = (s) => {
    setAddress(s.label);
    setSuggestions([]);
    if (s.suburb) setResult({ status: "in", suburb: s.suburb, town_water: s.town_water, pure_water: s.pure_water });
    else setResult({ status: "out" });
  };

  const bookWithAddress = () => navigate(address.trim() ? `/book?address=${encodeURIComponent(address)}` : "/book");

  return (
    <div className="bg-white overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[700px] flex items-center">
        <img src={HERO} alt="Blake's tanker filling a pool" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,26,51,0.9) 0%, rgba(7,26,51,0.6) 45%, rgba(7,26,51,0.12) 100%)" }} />
        <div className="relative max-w-[1320px] w-full mx-auto px-6 sm:px-10 pt-28 pb-24">
          <div className="max-w-[580px]">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-0.5 w-8 bg-cyan-400 rounded-full" />
              <span className="text-[12px] font-bold tracking-[2px] uppercase text-cyan-200">Auckland's Trusted Water Delivery</span>
            </div>
            <h1 className="font-heading font-extrabold text-white leading-[0.98] tracking-tight text-5xl sm:text-6xl lg:text-[72px]">
              Water Delivery<br />
              <span style={{ background: "linear-gradient(90deg,#26C6F7,#087FF5)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Made Easy</span>
            </h1>
            <p className="mt-5 text-lg text-slate-200 leading-relaxed max-w-lg">
              Fast, reliable delivery of up to 11,000L throughout Auckland. Perfect for pools, tanks, construction sites and more.
            </p>

            {/* ADDRESS CHECKER */}
            <div className="mt-7 max-w-md" data-testid="hero-address-checker">
              <p className="text-sm font-semibold text-cyan-100 mb-2">Start typing your address to check if we deliver to you</p>
              <div className="relative">
                <div className="flex items-center gap-2 bg-white rounded-full pl-5 pr-2 py-1.5 shadow-lg shadow-slate-950/25">
                  {searching ? <Loader2 className="w-5 h-5 text-blue-500 animate-spin shrink-0" /> : <MapPin className="w-5 h-5 text-blue-600 shrink-0" />}
                  <input value={address} onChange={onAddress} data-testid="hero-address-input"
                    placeholder="e.g. 12 Main Road, Kumeū" autoComplete="off"
                    className="flex-1 min-w-0 py-2 outline-none text-sm text-slate-800 bg-transparent" />
                  <button onClick={bookWithAddress} data-testid="hero-address-check-btn"
                    className="hidden sm:flex items-center gap-1.5 text-white font-semibold text-sm px-5 h-11 rounded-full shrink-0" style={BTN}>
                    Check <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                {suggestions.length > 0 && (
                  <div className="absolute z-30 mt-2 w-full bg-white border border-slate-100 rounded-2xl shadow-xl overflow-hidden" data-testid="hero-address-suggestions">
                    {suggestions.map((s, i) => (
                      <button key={i} onClick={() => pickAddress(s)} data-testid={`hero-address-suggestion-${i}`}
                        className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-slate-50 last:border-0 flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-700 truncate">{s.label}</span>
                        {s.suburb ? <span className="text-xs font-semibold text-green-600 shrink-0">${s.town_water}+</span>
                          : <span className="text-xs text-red-400 shrink-0">Out of area</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {result?.status === "in" && (
                <div className="mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3" data-testid="hero-in-area">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  <span className="text-sm text-green-800"><b>Yes! We deliver to {result.suburb}</b> — from ${result.town_water}/load</span>
                  <button onClick={bookWithAddress} className="ml-auto text-xs font-bold text-green-700 underline shrink-0" data-testid="hero-in-area-book">Book now</button>
                </div>
              )}
              {result?.status === "out" && (
                <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="hero-out-area">
                  <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                  <span className="text-sm text-red-700">Sorry, that address is outside our service area. <a href="tel:0226333426" className="font-semibold underline">Call us</a> and we'll see what we can do.</span>
                </div>
              )}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => goBook()} data-testid="hero-book-btn" className="flex items-center gap-2 text-white font-semibold px-7 h-[54px] rounded-full btn-shine" style={BTN}>
                <Calendar className="w-5 h-5" /> Book a Delivery →
              </button>
              <button onClick={() => goBook()} data-testid="hero-watch-btn" className="flex items-center gap-2 text-white font-semibold px-6 h-[54px] rounded-full border border-white/40 bg-white/10 backdrop-blur hover:bg-white/20 transition-colors">
                <Play className="w-5 h-5" /> Watch Video
              </button>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-4">
              {trust.map((t) => (
                <div key={t.title} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center shrink-0"><t.icon className="w-3.5 h-3.5 text-cyan-300" /></div>
                  <div><div className="text-white font-bold text-[13px] leading-tight">{t.title}</div><div className="text-slate-300 text-[11px]">{t.sub}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="relative z-10 -mt-14 max-w-[1280px] mx-auto px-6">
        <div className="bg-white rounded-[20px] shadow-[0_18px_40px_rgba(15,23,42,0.11)] border border-[rgba(15,23,42,0.08)] grid grid-cols-2 lg:grid-cols-4 lg:divide-x divide-slate-100">
          {stats.map((s) => (
            <div key={s.accent} className="flex items-center gap-4 p-6 sm:p-7">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${s.color}`}><s.icon className="w-6 h-6" /></div>
              <div>
                <div className="font-heading font-extrabold text-lg text-[#071A33] leading-tight">{s.value}</div>
                <div className="text-sm font-semibold text-blue-600">{s.accent}</div>
                <div className="text-xs text-slate-500">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section id="services" className="bg-[#F6F9FD] pt-[72px] pb-[72px] -mt-6">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10">
          <div className="text-center mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[1.5px] uppercase text-blue-600 bg-blue-50 rounded-full px-4 py-1.5 mb-4">Our Services</span>
            <h2 className="font-heading text-3xl sm:text-[42px] font-extrabold text-[#071A33]">Water When You Need It</h2>
            <div className="w-16 h-1 rounded-full mx-auto mt-4 mb-5" style={{ background: "linear-gradient(90deg,#087FF5,#26C6F7)" }} />
            <p className="text-slate-500 max-w-[680px] mx-auto text-[15px]">Reliable water delivery for homes, businesses, construction sites and more — delivered safely and efficiently across Auckland.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <motion.button key={s.title} {...fade} transition={{ duration: 0.4, delay: i * 0.08 }} onClick={() => goBook(s.type)} data-testid={`service-card-${s.type}`}
                className="group text-left bg-white rounded-[20px] border border-[rgba(15,23,42,0.08)] shadow-[0_12px_30px_rgba(15,23,42,0.07)] overflow-hidden transition-all duration-[250ms] ease-out hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.11)]">
                <div className="relative">
                  <div className="h-44 overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-[250ms] ease-out group-hover:scale-[1.03]" />
                  </div>
                  <div className={`absolute -bottom-6 left-5 w-12 h-12 rounded-full ${s.color} flex items-center justify-center shadow-lg ring-4 ring-white`}><s.icon className="w-5 h-5 text-white" /></div>
                </div>
                <div className="p-5 pt-9">
                  <h3 className="font-heading font-bold text-[18px] text-[#071A33]">{s.title}</h3>
                  <p className="text-[14px] text-slate-500 mt-1.5 leading-relaxed min-h-[42px]">{s.desc}</p>
                  <div className="mt-4 flex justify-end">
                    <span className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-all duration-[250ms]"><ArrowRight className="w-4 h-4 text-blue-600 transition-transform duration-[250ms] group-hover:translate-x-0.5" /></span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* USE-CASE STRIP */}
          <div className="mt-12 text-center">
            <span className="inline-block text-[11px] font-bold tracking-[1.5px] uppercase text-blue-600 mb-5">What We Deliver Water For</span>
            <div className="flex flex-wrap justify-center gap-3">
              {useCases.map((u) => (
                <div key={u.label} className="flex items-center gap-2.5 bg-white border border-[rgba(15,23,42,0.08)] rounded-full pl-2 pr-5 py-2 shadow-[0_6px_16px_rgba(15,23,42,0.05)]" data-testid={`usecase-${u.label.toLowerCase().replace(/ /g, "-")}`}>
                  <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center"><u.icon className="w-4 h-4 text-blue-600" /></span>
                  <span className="text-sm font-semibold text-[#071A33]">{u.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="why" className="max-w-[1320px] mx-auto px-6 sm:px-10 py-[72px]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade} className="rounded-[20px] overflow-hidden h-[400px] shadow-[0_18px_40px_rgba(15,23,42,0.14)]">
            <img src={POOL_IMG} alt="Blake's water truck" className="w-full h-full object-cover" />
          </motion.div>
          <div>
            <span className="text-xs font-bold tracking-widest text-blue-600">WHY BLAKE'S</span>
            <h2 className="font-heading text-3xl sm:text-[40px] font-extrabold text-[#071A33] mt-2 leading-tight">Reliable Water Delivery Across Auckland</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {whyList.map((w) => (
                <div key={w} className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0"><CheckCircle2 className="w-4 h-4 text-blue-600" /></span>
                  <span className="font-semibold text-[#071A33] text-sm">{w}</span>
                </div>
              ))}
            </div>
            <button onClick={() => goBook()} className="mt-8 flex items-center gap-2 text-white font-semibold px-7 h-[52px] rounded-full btn-shine" style={BTN} data-testid="why-book-btn">
              <Calendar className="w-5 h-5" /> Book a Delivery →
            </button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F6F9FD] py-[72px]">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest text-blue-600 bg-blue-50 rounded-full px-4 py-1.5 mb-4">HOW IT WORKS</span>
            <h2 className="font-heading text-3xl sm:text-[42px] font-extrabold text-[#071A33]">Water Delivered in 3 Easy Steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {steps.map((s) => (
              <motion.div key={s.n} {...fade} className="bg-white rounded-2xl border border-[rgba(15,23,42,0.08)] shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center font-heading font-extrabold text-xl text-white mb-5" style={BTN}>{s.n}</div>
                <h3 className="font-heading font-bold text-lg text-[#071A33]">{s.title}</h3>
                <p className="text-sm text-slate-500 mt-2">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING / COVERAGE MAP */}
      <ServiceArea onBook={() => goBook()} />

      {/* REVIEWS */}
      <section id="reviews" className="bg-[#F6F9FD] py-[72px]">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10">
          <div className="text-center mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[1.5px] uppercase text-blue-600 bg-blue-50 rounded-full px-4 py-1.5 mb-4">Customer Reviews</span>
            <h2 className="font-heading text-3xl sm:text-[42px] font-extrabold text-[#071A33]">Trusted By Auckland Customers</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <motion.div key={r.name} {...fade} transition={{ duration: 0.4, delay: i * 0.08 }} data-testid={`review-card-${i}`}
                className="bg-white rounded-[20px] border border-[rgba(15,23,42,0.08)] shadow-[0_12px_30px_rgba(15,23,42,0.07)] p-6 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">{[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google review" className="w-4 h-4" />
                </div>
                <p className="text-[15px] text-slate-700 leading-relaxed flex-1">"{r.text}"</p>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-white text-sm shrink-0" style={BTN}>{r.name.split(" ").map((x) => x[0]).join("")}</span>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-[#071A33] text-sm">{r.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.loc}</div>
                  </div>
                  <span className="ml-auto text-[11px] font-semibold text-green-600 flex items-center gap-1 shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(120deg,#087FF5,#071A33)" }}>
        <div className="absolute -right-16 -top-16 w-96 h-96 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute -left-10 bottom-0 w-72 h-72 rounded-full bg-blue-300/10 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 0 1px, transparent 1px), radial-gradient(circle at 70% 65%, #fff 0 1px, transparent 1px)", backgroundSize: "46px 46px" }} />
        <Droplets className="absolute right-10 bottom-6 w-40 h-40 text-white/[0.05]" />
        <div className="relative max-w-[1320px] mx-auto px-6 sm:px-10 py-[68px] text-center">
          <Droplets className="w-12 h-12 text-cyan-300 mx-auto mb-4" />
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-white">Need Water Delivered?</h2>
          <p className="text-blue-100 mt-3 text-lg">Book your delivery online and we'll take care of the rest.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button onClick={() => goBook()} className="flex items-center gap-2 bg-white text-blue-600 font-bold px-9 h-[58px] rounded-full shadow-[0_16px_40px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 transition-transform text-[17px]" data-testid="cta-book-delivery-btn">
              <Calendar className="w-5 h-5" /> Book a Delivery →
            </button>
            <a href="tel:0226333426" className="flex items-center gap-2 text-white font-semibold px-7 h-[58px] rounded-full border border-white/40 bg-white/10 hover:bg-white/20 transition-colors">
              <Phone className="w-5 h-5" /> Call Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ServiceArea({ onBook }) {
  const [config, setConfig] = useState(null);
  useEffect(() => { api.get("/service/config").then(({ data }) => setConfig(data && data.depot ? data : null)).catch(() => {}); }, []);
  if (!config) return null;
  return (
    <section id="pricing" className="max-w-[1320px] mx-auto px-6 sm:px-10 py-[72px]">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-[11px] font-bold tracking-[1.5px] uppercase text-blue-600">Coverage & Pricing</span>
          <h2 className="font-heading text-3xl sm:text-[40px] font-extrabold text-[#071A33] mt-2">Our Service Area</h2>
          <div className="w-16 h-1 rounded-full mt-4 mb-6" style={{ background: "linear-gradient(90deg,#087FF5,#26C6F7)" }} />
          <p className="text-slate-600 mb-6 text-[15px]">We deliver Town Water and Pure Water across <span className="font-semibold text-[#071A33]">{config.zones?.length || 0}+ North-West Auckland suburbs</span>. Pick your suburb when booking for an instant price.</p>
          <div className="grid grid-cols-2 gap-2 mb-8 max-h-64 overflow-y-auto pr-2 -mr-2" data-testid="coverage-zone-list">
            {(config.zones || []).map((z) => (
              <div key={z.id} className="flex items-center justify-between bg-slate-50 border border-[rgba(15,23,42,0.06)] rounded-xl px-3 py-2.5 hover:bg-blue-50 hover:border-blue-100 transition-colors" data-testid={`coverage-zone-${z.id}`}>
                <span className="flex items-center gap-2 font-semibold text-[#071A33] text-sm"><MapPin className="w-3.5 h-3.5 text-blue-600" /> {z.name}</span>
                <span className="text-xs font-semibold text-slate-500">${z.town_water}</span>
              </div>
            ))}
          </div>
          <button onClick={onBook} className="flex items-center gap-2 text-white font-semibold px-7 h-[52px] rounded-full btn-shine" style={BTN} data-testid="coverage-book-btn">
            Get your quote <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="rounded-[20px] overflow-hidden shadow-[0_18px_40px_rgba(15,23,42,0.11)] border border-[rgba(15,23,42,0.08)]">
          <ServiceMap depot={config.depot} serviceRadiusKm={config.service_radius_km} bands={[]} height={440} />
        </div>
      </div>
    </section>
  );
}
